import { useTranslation } from "react-i18next";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSession } from "@/providers/AppSessionProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ExternalLink, User, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import AdminAnalyticsGraph from "@/components/AdminAnalyticsGraph";
import AdminCreatorCodes from "@/components/AdminCreatorCodes";
import AdminOnboardingAnalytics from "@/components/AdminOnboardingAnalytics";
import AdminABTestAnalytics from "@/components/AdminABTestAnalytics";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const ADMIN_EMAIL = "aaronmichaelfrost@gmail.com";
const USERS_PER_PAGE = 20;

interface UserData {
  id: string;
  email: string;
  username: string | null;
  is_premium: boolean;
  created_at: string;
  credits: number;
  is_banned: boolean;
  referral_count: number;
  report_count: number;
  enabled_metrics_count: number;
  ai_usage_count: number;
  total_input_tokens: number;
  total_output_tokens: number;
}

interface UserReport {
  id: string;
  reporter_id: string;
  reporter_email: string;
  reporter_username: string | null;
  reason: string;
  details: string | null;
  status: string;
  created_at: string;
}

interface ContentReport {
  id: string;
  reporter_user_id: string;
  reported_user_id: string;
  content_type: string;
  content_id: string;
  reason: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reporter_username: string | null;
  reported_username: string | null;
  content_details: any;
}

const Admin = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { ready, session, isAuthenticated } = useAppSession();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [banLoading, setBanLoading] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'credits' | 'premium' | 'reports' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [premiumFilter, setPremiumFilter] = useState<'all' | 'premium' | 'free'>('all');
  const [excludeDeleted, setExcludeDeleted] = useState(true);
  const [viewingReportsFor, setViewingReportsFor] = useState<{ userId: string; username: string | null } | null>(null);
  const [userReports, setUserReports] = useState<UserReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [contentReports, setContentReports] = useState<ContentReport[]>([]);
  const [loadingContentReports, setLoadingContentReports] = useState(false);
  const [contentReportFilter, setContentReportFilter] = useState<'all' | 'pending' | 'reviewed' | 'actioned' | 'dismissed'>('pending');

  // Check authentication and authorization
  useEffect(() => {
    if (ready !== 'ready') return;
    
    if (!isAuthenticated) {
      console.log("[Admin] No session found, redirecting to auth");
      navigate("/auth", { replace: true });
      return;
    }

    const userEmail = session?.user?.email;
    if (userEmail !== ADMIN_EMAIL) {
      console.log("[Admin] Unauthorized access attempt by:", userEmail);
      toast.error(t('notifications.toast.unauthorized_access'));
      navigate("/", { replace: true });
    }
  }, [ready, isAuthenticated, session, navigate]);

  // Fetch users
  useEffect(() => {
    if (ready !== 'ready' || !isAuthenticated || session?.user?.email !== ADMIN_EMAIL) {
      return;
    }

    fetchUsers();
    fetchContentReports();
  }, [ready, isAuthenticated, session, currentPage, searchTerm, sortBy, sortDirection, premiumFilter, excludeDeleted]);

  useEffect(() => {
    if (ready !== 'ready' || !isAuthenticated || session?.user?.email !== ADMIN_EMAIL) {
      return;
    }
    fetchContentReports();
  }, [contentReportFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Build query with deleted account filtering
      let query = supabase
        .from('profiles')
        .select(`
          id,
          email,
          username,
          is_premium,
          created_at
        `, { count: 'exact' });

      // Filter out deleted accounts by checking auth.users.deleted_at
      if (excludeDeleted) {
        // Get list of active (non-deleted) user IDs
        const { data: activeUsers } = await supabase.rpc('get_active_user_ids_for_admin');
        if (activeUsers && activeUsers.length > 0) {
          query = query.in('id', activeUsers);
        } else if (activeUsers && activeUsers.length === 0) {
          // No active users, return empty
          setUsers([]);
          setTotalUsers(0);
          setLoading(false);
          return;
        }
      }

      // Apply search filter
      if (searchTerm) {
        query = query.or(`email.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`);
      }

      // Apply pagination
      const from = (currentPage - 1) * USERS_PER_PAGE;
      const to = from + USERS_PER_PAGE - 1;
      query = query.range(from, to).order('created_at', { ascending: false });

      const { data: profiles, error: profilesError, count } = await query;

      if (profilesError) throw profilesError;
      if (!profiles) {
        setUsers([]);
        setTotalUsers(0);
        return;
      }

      setTotalUsers(count || 0);

      // Fetch credits, ban status, and referral counts for each user
      const userIds = profiles.map(p => p.id);

      // Get credits
      const { data: creditsData } = await supabase
        .from('user_credits')
        .select('user_id, credits')
        .in('user_id', userIds);

      // Get ban status
      const { data: banData } = await supabase
        .from('user_bans')
        .select('user_id')
        .in('user_id', userIds);

      // Get referral counts (as referrer)
      const { data: referralData } = await supabase
        .from('referrals')
        .select('referrer_user_id')
        .in('referrer_user_id', userIds)
        .eq('credits_awarded', true);

      // Get report counts (as reported user)
      const { data: reportData } = await supabase
        .from('user_reports')
        .select('reported_user_id')
        .in('reported_user_id', userIds);

      // Get enabled metrics count (includes both built-in and custom metrics)
      const { data: metricsData } = await supabase
        .from('user_chart_preferences')
        .select('user_id, active_metrics')
        .in('user_id', userIds);

      // Get AI usage data (from free_tier_token_usage - tracks parse-quick-entry AI usage)
      const { data: tokenData } = await (supabase as any)
        .from('free_tier_token_usage')
        .select('user_id, input_tokens, output_tokens')
        .in('user_id', userIds);

      // Create lookup maps
      const creditsMap = new Map(creditsData?.map(c => [c.user_id, c.credits]) || []);
      const banSet = new Set(banData?.map(b => b.user_id) || []);
      const referralCountMap = new Map<string, number>();
      referralData?.forEach(r => {
        const count = referralCountMap.get(r.referrer_user_id) || 0;
        referralCountMap.set(r.referrer_user_id, count + 1);
      });
      const reportCountMap = new Map<string, number>();
      reportData?.forEach(r => {
        const count = reportCountMap.get(r.reported_user_id) || 0;
        reportCountMap.set(r.reported_user_id, count + 1);
      });
      const metricsCountMap = new Map<string, number>();
      metricsData?.forEach((m: any) => {
        const count = Array.isArray(m.active_metrics) ? m.active_metrics.length : 0;
        metricsCountMap.set(m.user_id, count);
      });
      // Aggregate token I/O and count AI usage by user_id
      const tokenByUserMap = new Map<string, { input: number; output: number; count: number }>();
      tokenData?.forEach((t: any) => {
        if (t.user_id) {
          const existing = tokenByUserMap.get(t.user_id) || { input: 0, output: 0, count: 0 };
          tokenByUserMap.set(t.user_id, {
            input: existing.input + (t.input_tokens || 0),
            output: existing.output + (t.output_tokens || 0),
            count: existing.count + 1,
          });
        }
      });

      // Combine data
      let enrichedUsers: UserData[] = profiles.map(profile => ({
        id: profile.id,
        email: profile.email || 'No email',
        username: profile.username,
        is_premium: profile.is_premium || false,
        created_at: profile.created_at || '',
        credits: creditsMap.get(profile.id) || 0,
        is_banned: banSet.has(profile.id),
        referral_count: referralCountMap.get(profile.id) || 0,
        report_count: reportCountMap.get(profile.id) || 0,
        enabled_metrics_count: metricsCountMap.get(profile.id) || 0,
        ai_usage_count: tokenByUserMap.get(profile.id)?.count || 0,
        total_input_tokens: tokenByUserMap.get(profile.id)?.input || 0,
        total_output_tokens: tokenByUserMap.get(profile.id)?.output || 0,
      }));

      // Apply premium filter
      if (premiumFilter === 'premium') {
        enrichedUsers = enrichedUsers.filter(u => u.is_premium);
      } else if (premiumFilter === 'free') {
        enrichedUsers = enrichedUsers.filter(u => !u.is_premium);
      }

      // Apply sorting
      if (sortBy === 'credits') {
        enrichedUsers.sort((a, b) => {
          return sortDirection === 'asc' ? a.credits - b.credits : b.credits - a.credits;
        });
      } else if (sortBy === 'premium') {
        enrichedUsers.sort((a, b) => {
          const aVal = a.is_premium ? 1 : 0;
          const bVal = b.is_premium ? 1 : 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
      } else if (sortBy === 'reports') {
        enrichedUsers.sort((a, b) => {
          return sortDirection === 'asc' ? a.report_count - b.report_count : b.report_count - a.report_count;
        });
      }

      setUsers(enrichedUsers);
    } catch (error) {
      console.error("[Admin] Error fetching users:", error);
      toast.error(t('notifications.toast.failed_to_load_users'));
    } finally {
      setLoading(false);
    }
  };

  const handleBanToggle = async (userId: string, currentBanStatus: boolean) => {
    try {
      setBanLoading(userId);

      if (currentBanStatus) {
        // Unban user - delete from user_bans
        const { error } = await supabase
          .from('user_bans')
          .delete()
          .eq('user_id', userId);

        if (error) throw error;
        toast.success(t('notifications.toast.user_unbanned'));
      } else {
        // Ban user - insert into user_bans
        const { error } = await supabase
          .from('user_bans')
          .insert({
            user_id: userId,
            reason: 'Banned by admin',
            banned_by: session?.user?.id,
          });

        if (error) throw error;
        toast.success(t('notifications.toast.user_banned'));
      }

      // Refresh users list
      await fetchUsers();
    } catch (error) {
      console.error("[Admin] Error toggling ban:", error);
      toast.error(t('notifications.toast.failed_to_update_ban_status'));
    } finally {
      setBanLoading(null);
    }
  };

  const handleSimulateUser = (userId: string, username: string | null) => {
    // Store the admin session and simulate user
    localStorage.setItem('admin_simulate_user_id', userId);
    toast.success(`Now simulating user: ${username || userId}`);
    
    // Navigate to home page where the simulation will take effect
    // The AdminSimulateBanner will show at the top and allow exiting simulation
    window.location.href = '/';
  };

  const handleViewReports = async (userId: string, username: string | null) => {
    try {
      setLoadingReports(true);
      setViewingReportsFor({ userId, username });

      // Fetch reports for this user
      const { data: reports, error } = await supabase
        .from('user_reports')
        .select(`
          id,
          reporter_id,
          reason,
          details,
          status,
          created_at
        `)
        .eq('reported_user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch reporter profiles
      const reporterIds = reports?.map(r => r.reporter_id) || [];
      const { data: reporters } = await supabase
        .from('profiles')
        .select('id, email, username')
        .in('id', reporterIds);

      const reporterMap = new Map(reporters?.map(r => [r.id, r]) || []);

      // Combine data
      const enrichedReports: UserReport[] = (reports || []).map(report => ({
        id: report.id,
        reporter_id: report.reporter_id,
        reporter_email: reporterMap.get(report.reporter_id)?.email || 'Unknown',
        reporter_username: reporterMap.get(report.reporter_id)?.username || null,
        reason: report.reason,
        details: report.details,
        status: report.status,
        created_at: report.created_at,
      }));

      setUserReports(enrichedReports);
    } catch (error) {
      console.error("[Admin] Error fetching reports:", error);
      toast.error(t('notifications.toast.failed_to_load_reports'));
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchContentReports = async () => {
    try {
      setLoadingContentReports(true);

      // Build query
      let query = (supabase as any)
        .from('content_reports')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply status filter
      if (contentReportFilter !== 'all') {
        query = query.eq('status', contentReportFilter);
      }

      const { data: reports, error } = await query.limit(50);

      if (error) throw error;

      if (!reports || reports.length === 0) {
        setContentReports([]);
        return;
      }

      // Get reporter and reported user info
      const reporterIds = [...new Set(reports.map(r => r.reporter_user_id))];
      const reportedIds = [...new Set(reports.map(r => r.reported_user_id))];
      const allUserIds = [...new Set([...reporterIds, ...reportedIds])];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', allUserIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Get content details for progress snaps
      const snapIds = reports
        .filter(r => r.content_type === 'progress_snap')
        .map(r => r.content_id);

      let snapDetails: any = {};
      if (snapIds.length > 0) {
        const { data: snaps } = await (supabase as any)
          .from('progress_snaps')
          .select('id, snap_date, text_content, image_url, metric_name')
          .in('id', snapIds);

        snapDetails = new Map(snaps?.map((s: any) => [s.id, s]) || []);
      }

      // Combine data
      const enrichedReports: ContentReport[] = reports.map(report => ({
        ...report,
        reporter_username: profileMap.get(report.reporter_user_id)?.username || null,
        reported_username: profileMap.get(report.reported_user_id)?.username || null,
        content_details: report.content_type === 'progress_snap' ? snapDetails.get(report.content_id) : null,
      }));

      setContentReports(enrichedReports);
    } catch (error) {
      console.error("[Admin] Error fetching content reports:", error);
      toast.error(t('notifications.toast.failed_to_load_content_reports'));
    } finally {
      setLoadingContentReports(false);
    }
  };

  const handleUpdateReportStatus = async (reportId: string, newStatus: string, adminNotes?: string) => {
    try {
      const updateData: any = { status: newStatus, reviewed_at: new Date().toISOString(), reviewed_by: session?.user?.id };
      if (adminNotes) {
        updateData.admin_notes = adminNotes;
      }

      const { error } = await (supabase as any)
        .from('content_reports')
        .update(updateData)
        .eq('id', reportId);

      if (error) throw error;

      toast.success(`Report marked as ${newStatus}`);
      await fetchContentReports();
    } catch (error) {
      console.error("[Admin] Error updating report:", error);
      toast.error(t('notifications.toast.failed_to_update_report'));
    }
  };

  const handleSort = (column: 'credits' | 'premium' | 'reports') => {
    if (sortBy === column) {
      // Toggle direction or clear sort
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        setSortBy(null);
        setSortDirection('desc');
      }
    } else {
      // Set new sort column
      setSortBy(column);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column: 'credits' | 'premium' | 'reports') => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4 ml-1" /> : 
      <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);

  // Show loading while checking auth
  if (ready === 'checking' || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <GitFitGridLoader className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('ui.text.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{t('ui.text.admin_dashboard')}</h1>
        <p className="text-muted-foreground">{t('ui.text.manage_users_and_system_settings')}</p>
      </div>

      {/* Analytics Graph */}
      <div className="mb-6">
        <AdminAnalyticsGraph />
      </div>

      {/* Onboarding Analytics */}
      <div className="mb-6">
        <AdminOnboardingAnalytics />
      </div>

      {/* A/B Test: Landing Page */}
      <div className="mb-6">
        <AdminABTestAnalytics />
      </div>

      {/* Creator Referral Codes */}
      <div className="mb-6">
        <AdminCreatorCodes />
      </div>

      {/* Content Reports */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('ui.text.content_reports')}</span>
            <Select value={contentReportFilter} onValueChange={(value: any) => setContentReportFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('forms.placeholders.filter_by_status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('ui.text.all_reports')}</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="actioned">Actioned</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingContentReports ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : contentReports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('ui.text.no_content_reports_found')}</div>
          ) : (
            <div className="space-y-4">
              {contentReports.map((report) => (
                <Card key={report.id} className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={report.status === 'pending' ? 'destructive' : 'secondary'}>
                            {report.status}
                          </Badge>
                          <span className="text-sm font-medium">{report.content_type}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(report.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {report.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateReportStatus(report.id, 'reviewed')}
                            >{t('ui.text.mark_reviewed')}</Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleUpdateReportStatus(report.id, 'actioned')}
                            >{t('ui.text.take_action')}</Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUpdateReportStatus(report.id, 'dismissed')}
                            >
                              Dismiss
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium">{t('ui.text.reporter')}</p>
                        <p className="text-muted-foreground">@{report.reporter_username || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="font-medium">{t('ui.text.reported_user')}</p>
                        <p className="text-muted-foreground">@{report.reported_username || 'Unknown'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium">{t('ui.text.reason')}</p>
                      <p className="text-sm text-muted-foreground">{report.reason}</p>
                    </div>

                    {report.content_details && report.content_type === 'progress_snap' && (
                      <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                        <p className="text-sm font-medium">{t('ui.text.progress_snap_details')}</p>
                        <div className="text-xs space-y-1">
                          <p><span className="font-medium">{t('ui.text.metric')}</span> {report.content_details.metric_name}</p>
                          <p><span className="font-medium">{t('ui.text.date')}</span> {report.content_details.snap_date}</p>
                          {report.content_details.text_content && (
                            <p><span className="font-medium">{t('pages.admin.text')}</span> {report.content_details.text_content.substring(0, 100)}{report.content_details.text_content.length > 100 ? '...' : ''}</p>
                          )}
                          {report.content_details.image_url && (
                            <div className="mt-2">
                              <img 
                                src={report.content_details.image_url} 
                                alt={t('accessibility.labels.reported_content')} 
                                className="max-w-xs rounded"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {report.admin_notes && (
                      <div className="bg-blue-500/10 p-3 rounded-lg">
                        <p className="text-sm font-medium">{t('ui.text.admin_notes')}</p>
                        <p className="text-sm text-muted-foreground">{report.admin_notes}</p>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('ui.text.user_management')}</span>
            <Badge variant="secondary">{totalUsers} total users</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="mb-4 flex gap-4 items-center flex-wrap">
            <Input
              placeholder={t('forms.placeholders.search_by_email_or_username')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="max-w-md"
            />
            <Select
              value={premiumFilter}
              onValueChange={(value: 'all' | 'premium' | 'free') => {
                setPremiumFilter(value);
                setCurrentPage(1); // Reset to first page on filter
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('forms.placeholders.filter_by_status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('ui.text.all_users')}</SelectItem>
                <SelectItem value="premium">{t('ui.text.premium_only')}</SelectItem>
                <SelectItem value="free">{t('ui.text.free_only')}</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Switch
                id="exclude-deleted"
                checked={excludeDeleted}
                onCheckedChange={(checked) => {
                  setExcludeDeleted(checked);
                  setCurrentPage(1);
                }}
              />
              <label
                htmlFor="exclude-deleted"
                className="text-sm font-medium cursor-pointer"
              >{t('ui.text.hide_deleted_accounts')}</label>
            </div>
          </div>

          {/* Users Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 p-0 hover:bg-transparent font-semibold"
                      onClick={() => handleSort('credits')}
                    >
                      <span className="flex items-center">
                        Credits
                        {getSortIcon('credits')}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead>Referrals</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 p-0 hover:bg-transparent font-semibold"
                      onClick={() => handleSort('reports')}
                    >
                      <span className="flex items-center">
                        Reports
                        {getSortIcon('reports')}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead>Metrics</TableHead>
                  <TableHead>{t('ui.text.ai_uses')}</TableHead>
                  <TableHead>{t('ui.text.token_i_o')}</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 p-0 hover:bg-transparent font-semibold"
                      onClick={() => handleSort('premium')}
                    >
                      <span className="flex items-center">
                        Premium
                        {getSortIcon('premium')}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead>Banned</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">{t('ui.text.no_users_found')}</TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-mono text-sm">{user.email}</TableCell>
                      <TableCell>
                        {user.username ? (
                          <span className="font-medium">{user.username}</span>
                        ) : (
                          <span className="text-muted-foreground italic">{t('ui.text.no_username')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.credits}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.referral_count}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={user.report_count > 0 ? "destructive" : "outline"}
                            className={user.report_count > 0 ? "cursor-pointer" : ""}
                            onClick={() => user.report_count > 0 && handleViewReports(user.id, user.username)}
                          >
                            {user.report_count}
                          </Badge>
                          {user.report_count > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewReports(user.id, user.username)}
                              className="h-6 px-2"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.enabled_metrics_count}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.ai_usage_count}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-1">
                          <div>{t('ui.text.in')}<span className="font-mono">{user.total_input_tokens.toLocaleString()}</span></div>
                          <div>{t('ui.text.out')}<span className="font-mono">{user.total_output_tokens.toLocaleString()}</span></div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.is_premium ? (
                          <Badge className="bg-yellow-500">Premium</Badge>
                        ) : (
                          <Badge variant="outline">Free</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={user.is_banned}
                            onCheckedChange={() => handleBanToggle(user.id, user.is_banned)}
                            disabled={banLoading === user.id}
                          />
                          {user.is_banned && (
                            <Badge variant="destructive">Banned</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSimulateUser(user.id, user.username)}
                            title={t('accessibility.labels.simulate_this_user')}
                          >
                            <User className="h-4 w-4 mr-1" />
                            Simulate
                          </Button>
                          {user.username && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`/${user.username}`, '_blank')}
                              title={t('accessibility.labels.view_profile')}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Reports Dialog */}
      <Dialog open={viewingReportsFor !== null} onOpenChange={() => setViewingReportsFor(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Reports for {viewingReportsFor?.username || viewingReportsFor?.userId}
            </DialogTitle>
            <DialogDescription>{t('ui.text.view_all_reports_submitted_against_this_user')}</DialogDescription>
          </DialogHeader>

          {loadingReports ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : userReports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('ui.text.no_reports_found_for_this_user')}</div>
          ) : (
            <div className="space-y-4">
              {userReports.map((report) => (
                <Card key={report.id} className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold">
                          Reported by: {report.reporter_username || report.reporter_email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(report.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge variant={report.status === 'pending' ? 'secondary' : 'outline'}>
                        {report.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('ui.text.reason')}</p>
                      <p className="text-sm text-muted-foreground">{report.reason}</p>
                    </div>
                    {report.details && (
                      <div>
                        <p className="text-sm font-medium">{t('ui.text.details')}</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {report.details}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
